import { Route } from '@/services/routes/route.vm';
import * as vm from '@/services/stages/stage.vm';
import update from 'immutability-helper';

export type StagesState = Record<Route['id'], { stages: vm.Stage[]; hasLoaded?: boolean }>;

export const stagesInitialState: StagesState = {};

const orderStages = (contents: vm.Stage[] | undefined): vm.Stage[] => {
  if (!contents) return [];
  return contents.sort((a, b) => {
    return (a.order ?? 0) - (b.order ?? 0);
  });
};

export enum StagesActionTypes {
  LOAD_ROUTE_STAGES = 'LOAD_ROUTE_STAGES',
  LOAD_STAGE = 'LOAD_STAGE',
  ADD_STAGE = 'ADD_STAGE',
  UPDATE_STAGE = 'UPDATE_STAGE',
  DELETE_STAGE = 'DELETE_STAGE',
}

export interface StageAction<T = any> {
  type: StagesActionTypes;
  payload?: T;
}

const updateStateByAction: Record<
  StagesActionTypes,
  (state: StagesState, action: StageAction) => StagesState
> = {
  [StagesActionTypes.LOAD_ROUTE_STAGES]: (
    state,
    action: StageAction<{
      routeId: Route['id'];
      stages: vm.Stage[];
    }>,
  ) => {
    const { payload } = action;
    if (!payload) return state;
    return { ...state, [payload.routeId]: { stages: orderStages(payload.stages), hasLoaded: true } };
  },
  [StagesActionTypes.LOAD_STAGE]: (
    state,
    action: StageAction<{
      routeId: Route['id'];
      stageId: vm.Stage['id'];
      stage: vm.Stage;
    }>,
  ) => {
    const { payload } = action;
    if (!payload) return state;
    const { routeId, stageId, stage } = payload;
    const routeStages = state[routeId].stages ?? [];
    const stageIndex = routeStages.findIndex((stage) => stage.id === stageId);
    let newStages;
    if (routeStages.length === 0 || stageIndex === -1) {
      newStages = [...routeStages, stage];
    } else {
      routeStages[stageIndex] = stage;
      newStages = [...routeStages];
    }
    return update(state, { [routeId]: { stages: { $set: orderStages(newStages) } } });
  },
  [StagesActionTypes.ADD_STAGE]: (state, action: StageAction<{ routeId: Route['id']; stage: vm.Stage }>) => {
    const { payload } = action;
    if (!payload) return state;
    const { routeId, stage } = payload;
    return update(state, {
      [routeId]: {
        stages: { $push: [stage] },
      },
    });
  },
  [StagesActionTypes.UPDATE_STAGE]: (
    state,
    action: StageAction<{
      routeId: Route['id'];
      stage: Partial<vm.Stage>;
    }>,
  ) => {
    const { payload } = action;
    if (!payload) return state;
    const { routeId, stage } = payload;
    const routeStages = state[routeId].stages;
    if (!routeStages) return state;
    const newStages = routeStages?.map((prevStage) => {
      if (prevStage.id === stage.id) {
        const newContent = {
          ...prevStage,
          ...stage,
        };
        return newContent;
      }
      return prevStage;
    });
    return update(state, {
      [routeId]: {
        stages: { $set: orderStages(newStages) },
      },
    });
  },
  [StagesActionTypes.DELETE_STAGE]: (
    state,
    action: StageAction<{
      routeId: Route['id'];
      stageId: vm.Stage['id'];
    }>,
  ) => {
    const { payload } = action;
    if (!payload) return state;
    const { routeId, stageId } = payload;
    const routeStages = state[routeId].stages;
    if (!routeStages) return state;
    const newStages = routeStages?.filter((content) => content.id !== stageId);
    return update(state, {
      [routeId]: {
        stages: { $set: orderStages(newStages) },
      },
    });
  },
};

export const stagesDataReducer = (state: StagesState, action: StageAction) => {
  const { type: actionType } = action;
  const updateState = updateStateByAction[actionType];
  return updateState ? updateState(state, action) : state;
};
